import { z } from "zod";

/**
 * Account-level cohort selector. Shared by every arena-ops panel that accepts
 * one — the matchmaking panels deliberately do NOT, because match-level bot-ness
 * is `ArenaEntry.isBot` (point-in-time) and disagrees with the live pool on history.
 */
export const ArenaOpsCohortSchema = z.enum(["all", "real", "bots", "staff"]);
export type ArenaOpsCohort = z.infer<typeof ArenaOpsCohortSchema>;

/** Decimal-USDT string. Money never crosses the wire as a JS number. */
export const ArenaOpsUsdtSchema = z.string();

/** Opaque keyset cursor. Panels never parse it; they hand it back verbatim. */
export const ArenaOpsCursorSchema = z.string().min(1);

/** Inclusive-exclusive ISO-8601 window. Absent or over-long is rejected, never clamped. */
export const ArenaOpsRangeSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
});
export type ArenaOpsRange = z.infer<typeof ArenaOpsRangeSchema>;

/** Time bucket for every series panel. */
export const ArenaOpsBucketSchema = z.enum(["hour", "day"]);
export type ArenaOpsBucket = z.infer<typeof ArenaOpsBucketSchema>;

/** Keyset page envelope: `nextCursor === null` is the last page. */
export function arenaOpsPageOf<TRow extends z.ZodTypeAny>(row: TRow) {
  return z.object({ rows: z.array(row), nextCursor: z.string().nullable() });
}

/**
 * A movement as every drill-down renders it. `totalDeltaUsdt` is the LAG delta
 * over `freeAfter + lockedAfter`, NOT `amount`: a MATCHMAKING_LOCK reads −10.00
 * raw against a true delta of 0.00, and a movement can disagree with its own
 * snapshot delta.
 */
export const ArenaOpsMovementRowSchema = z.object({
  movementId: z.number().int(),
  hubUserId: z.number().int(),
  type: z.string(),
  amountUsdt: ArenaOpsUsdtSchema,
  totalDeltaUsdt: ArenaOpsUsdtSchema,
  freeAfterUsdt: ArenaOpsUsdtSchema,
  lockedAfterUsdt: ArenaOpsUsdtSchema,
  idempotencyKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type ArenaOpsMovementRow = z.infer<typeof ArenaOpsMovementRowSchema>;
